import { StandardEvents } from '@shopify/events';

// Homepage-only enhancement. Shopify's existing ProductFormComponent owns every cart request.
class OdCarousel extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.track = this.querySelector('[data-od-track]');
    this.slides = [...this.querySelectorAll('[data-od-slide]')];
    this.previous = this.querySelector('[data-od-prev]');
    this.next = this.querySelector('[data-od-next]');
    this.status = this.querySelector('[data-od-status]');
    this.pending ??= new Map();
    // A moved editor section keeps its in-flight request guard, without orphaned timers.
    for (const [form, record] of this.pending) {
      if (!record.timer) record.timer = setTimeout(() => this.settle(form, false, this.dataset.odError), Math.max(0, 30000 - (Date.now() - record.startedAt)));
    }
    if (!this.track || !this.slides.length) return;
    const controls = this.querySelector('[data-od-controls]');
    if (controls && !controls.hasAttribute('data-od-no-controls')) controls.hidden = false;
    this.previous.addEventListener('click', () => this.move(-1), options);
    this.next.addEventListener('click', () => this.move(1), options);
    this.track.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault(); this.scrollTo(event.key === 'Home' ? 0 : this.track.scrollWidth);
      }
    }, options);
    // Native Component delegates at document capture. Window capture must run first,
    // while the containment check keeps this guard exclusive to this carousel.
    window.addEventListener('submit', event => this.guardSubmit(event), { ...options, capture: true });
    this.addEventListener(StandardEvents.cartLinesUpdate, event => this.observeCart(event), options);
    this.addEventListener(StandardEvents.cartError, event => this.observeCartError(event), options);
    this.resize = new ResizeObserver(() => this.scheduleUpdate());
    this.resize.observe(this.track);
    this.update();
  }

  scheduleUpdate() {
    if (this.frame || !this.isConnected) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.isConnected) this.update(); });
  }

  update() {
    if (!this.track || !this.isConnected) return;
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const position = this.track.scrollLeft;
    const viewport = this.track.getBoundingClientRect();
    const visible = this.slides.map((slide, index) => {
      const rect = slide.getBoundingClientRect();
      return rect.right > viewport.left + 3 && rect.left < viewport.right - 3 ? index + 1 : null;
    }).filter(Boolean);
    // Complete geometry reads before state writes can invalidate layout.
    const atStart = position <= 2;
    const atEnd = position >= maximum - 2;
    if (this.previous.disabled !== atStart) this.previous.disabled = atStart;
    if (this.next.disabled !== atEnd) this.next.disabled = atEnd;
    if (visible.length && this.status) {
      const message = `${this.dataset.odStatusPrefix}: ${visible[0]}–${visible.at(-1)} de ${this.slides.length}`;
      if (this.status.textContent !== message) this.status.textContent = message;
    }
  }

  scrollTo(left) {
    const motionAllowed = document.documentElement.dataset.odMotion !== 'false' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left, behavior: motionAllowed ? 'smooth' : 'auto' });
  }

  move(direction) {
    const gap = parseFloat(getComputedStyle(this.track.querySelector('.od-carousel__list')).columnGap) || 0;
    this.scrollTo(this.track.scrollLeft + direction * (this.slides[0].getBoundingClientRect().width + gap));
  }

  guardSubmit(event) {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    const wrapper = form?.closest('[data-od-native-form]');
    if (!wrapper || !this.contains(wrapper) || !customElements.get('product-form-component')) return;
    if (this.pending.has(form)) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    if (event.defaultPrevented || !form.checkValidity()) return;
    const button = form.querySelector('[data-od-add]');
    if (!button || button.disabled) return;
    const record = { wrapper, button, label: button.getAttribute('aria-label'), serverError: '', timer: null, startedAt: Date.now() };
    this.pending.set(form, record);
    wrapper.setAttribute('data-od-pending', '');
    form.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-label', `${this.dataset.odLoading} ${record.label}`);
    button.removeAttribute('data-od-added');
    const notice = wrapper.querySelector('[ref="addToCartTextError"]');
    if (notice) notice.classList.add('hidden');
    // An unknown result must never trigger an automatic second add request.
    record.timer = setTimeout(() => this.settle(form, false, this.dataset.odError), 30000);
  }

  observeCart(event) {
    const wrapper = event.target.closest?.('[data-od-native-form]');
    const form = wrapper?.querySelector('form');
    const record = form && this.pending.get(form);
    if (!record || !event.promise) return;
    Promise.resolve(event.promise).then(
      result => this.settle(form, !result?.detail?.didError, record.serverError || this.dataset.odError),
      () => this.settle(form, false, record.serverError || this.dataset.odError)
    );
  }

  observeCartError(event) {
    const form = event.target.closest?.('[data-od-native-form]')?.querySelector('form');
    const record = form && this.pending.get(form);
    if (!record) return;
    const description = event.detail?.description;
    record.serverError = typeof description === 'string' ? description : this.dataset.odError;
  }

  settle(form, successful, message) {
    const record = this.pending.get(form);
    if (!record) return;
    clearTimeout(record.timer);
    this.pending.delete(form);
    record.wrapper.removeAttribute('data-od-pending');
    form.removeAttribute('aria-busy');
    record.button.removeAttribute('aria-disabled');
    record.button.setAttribute('aria-label', record.label);
    if (successful) {
      record.button.setAttribute('data-od-added', '');
      // This editable announcement follows the resolved native response only.
      const live = record.wrapper.querySelector('[ref="liveRegion"]');
      if (live && this.dataset.odSuccess) live.textContent = this.dataset.odSuccess;
      return;
    }
    const notice = record.wrapper.querySelector('[ref="addToCartTextError"]');
    if (notice) { notice.textContent = message; notice.classList.remove('hidden'); }
    const live = record.wrapper.querySelector('[ref="liveRegion"]');
    if (live) live.textContent = message;
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    for (const record of this.pending?.values() || []) { clearTimeout(record.timer); record.timer = null; }
  }
}

if (!customElements.get('od-carousel')) customElements.define('od-carousel', OdCarousel);
